import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Calendar, Trash2, Pencil, X } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function KujdestariMeetings() {
    const [params] = useSearchParams();
    const [classId, setClassId] = useState(params.get("class_id") || "");
    const [classes, setClasses] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState({ class_id: classId, date: todayISO(), time: "17:00", agenda: [{ order: 1, title: "", notes: "" }], minutes: "", conclusion: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const refresh = async () => {
        setLoading(true);
        try {
            const [m, c] = await Promise.all([
                api.get("/kujdestari/meetings", { params: classId ? { class_id: classId } : {} }),
                api.get("/kujdestari/my-classes"),
            ]);
            setMeetings(m.data); setClasses(c.data);
            if (!classId && c.data.length > 0) setClassId(c.data[0].id);
        } finally { setLoading(false); }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [classId]);

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            const payload = { ...form, class_id: classId, agenda: form.agenda.filter((a) => a.title.trim()) };
            if (edit) {
                await api.patch(`/kujdestari/meeting/${edit.id}`, payload);
            } else {
                await api.post("/kujdestari/meeting", payload);
            }
            setOpen(false); setEdit(null);
            setForm({ class_id: classId, date: todayISO(), time: "17:00", agenda: [{ order: 1, title: "", notes: "" }], minutes: "", conclusion: "" });
            refresh();
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };

    const startEdit = (m) => {
        setEdit(m);
        setForm({
            class_id: m.class_id, date: m.date, time: m.time || "17:00",
            agenda: (m.agenda?.length ? m.agenda : [{ order: 1, title: "", notes: "" }]),
            minutes: m.minutes || "", conclusion: m.conclusion || "",
        });
        setOpen(true);
    };

    const del = async (mid) => {
        if (!window.confirm("Fshi këtë mbledhje?")) return;
        await api.delete(`/kujdestari/meeting/${mid}`);
        refresh();
    };

    const addAgendaItem = () => setForm((s) => ({ ...s, agenda: [...s.agenda, { order: s.agenda.length + 1, title: "", notes: "" }] }));
    const removeAgendaItem = (i) => setForm((s) => ({ ...s, agenda: s.agenda.filter((_, idx) => idx !== i).map((a, idx) => ({ ...a, order: idx + 1 })) }));
    const updateAgenda = (i, k, v) => setForm((s) => ({ ...s, agenda: s.agenda.map((a, idx) => idx === i ? { ...a, [k]: v } : a) }));

    return (
        <Layout>
            <Link to="/kujdestari" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Mbledhjet me prindër</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Procesverbalet</h1>
                    <p className="text-slate-600 mt-2 text-sm">Cakto datën, rendin e ditës (max 5 pika), shkruaj diskutimin dhe konkluzionin</p>
                </div>
                <div className="flex gap-2">
                    {classes.length > 1 && (
                        <select value={classId} onChange={(e) => setClassId(e.target.value)} className="h-11 rounded-md border border-slate-300 bg-white text-sm px-3 font-semibold" data-testid="meet-class-select">
                            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    )}
                    <Button onClick={() => { setEdit(null); setForm({ class_id: classId, date: todayISO(), time: "17:00", agenda: [{ order: 1, title: "", notes: "" }], minutes: "", conclusion: "" }); setOpen(true); }} className="bg-oxford hover:bg-oxfordhover text-white gap-2" data-testid="meet-new">
                        <Plus className="w-4 h-4" /> Mbledhje e re
                    </Button>
                </div>
            </div>

            {loading ? <div>Duke ngarkuar…</div> : meetings.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
                    <Calendar className="w-10 h-10 text-slate-400 mx-auto mb-4" />
                    <div className="font-heading text-xl font-bold text-slate-800">Pa mbledhje ende</div>
                    <p className="text-sm text-slate-600 mt-1">Cakto mbledhjen e parë me prindërit (rekomandohet 1x në muaj).</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {meetings.map((m) => (
                        <div key={m.id} id={m.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`meet-${m.id}`}>
                            <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/40 flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-oxford" />
                                <span className="font-heading font-bold text-slate-900">{m.date}</span>
                                <span className="text-sm text-slate-500 font-mono">{m.time}</span>
                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-oxford/10 text-oxford font-bold">{m.class_name}</span>
                                <div className="ml-auto flex gap-1">
                                    <button onClick={() => startEdit(m)} className="p-1.5 rounded text-slate-500 hover:bg-slate-100" data-testid={`meet-edit-${m.id}`}><Pencil className="w-4 h-4" /></button>
                                    <button onClick={() => del(m.id)} className="p-1.5 rounded text-terracotta hover:bg-terracotta/10" data-testid={`meet-del-${m.id}`}><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                            <div className="p-4 sm:p-5">
                                {m.agenda?.length > 0 && (
                                    <div className="mb-4">
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Rendi i ditës</div>
                                        <ol className="space-y-1.5">
                                            {m.agenda.map((a) => (
                                                <li key={a.order} className="flex gap-2 text-sm">
                                                    <span className="font-bold text-oxford w-6 shrink-0">{a.order}.</span>
                                                    <div>
                                                        <div className="font-semibold text-slate-800">{a.title}</div>
                                                        {a.notes && <div className="text-xs text-slate-500 mt-0.5">{a.notes}</div>}
                                                    </div>
                                                </li>
                                            ))}
                                        </ol>
                                    </div>
                                )}
                                {m.minutes && (
                                    <div className="mb-3">
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Procesverbali</div>
                                        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{m.minutes}</p>
                                    </div>
                                )}
                                {m.conclusion && (
                                    <div className="bg-sage/5 border-l-4 border-sage rounded p-3">
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-sage mb-1">Konkluzioni</div>
                                        <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{m.conclusion}</p>
                                    </div>
                                )}
                                <div className="text-[10px] text-slate-400 mt-3">— {m.created_by_name}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEdit(null); }}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-heading">{edit ? "Edito mbledhjen" : "Mbledhje e re me prindër"}</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">Data</label>
                                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="meet-form-date" />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">Ora</label>
                                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="meet-form-time" />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Rendi i ditës (max 5)</label>
                                {form.agenda.length < 5 && <button type="button" onClick={addAgendaItem} className="text-[11px] text-oxford font-semibold hover:underline inline-flex items-center gap-1" data-testid="meet-add-agenda"><Plus className="w-3 h-3" /> Pikë</button>}
                            </div>
                            <div className="space-y-2">
                                {form.agenda.map((a, i) => (
                                    <div key={i} className="flex gap-2 items-start" data-testid={`meet-agenda-${i}`}>
                                        <span className="text-sm font-bold text-oxford pt-2 w-6 shrink-0">{a.order}.</span>
                                        <div className="flex-1 space-y-1">
                                            <Input placeholder="Titulli i pikës" value={a.title} onChange={(e) => updateAgenda(i, "title", e.target.value)} className="h-9 text-sm" />
                                            <Input placeholder="Shënime opsionale" value={a.notes} onChange={(e) => updateAgenda(i, "notes", e.target.value)} className="h-8 text-xs" />
                                        </div>
                                        {form.agenda.length > 1 && <button type="button" onClick={() => removeAgendaItem(i)} className="text-terracotta hover:bg-terracotta/10 rounded p-1 mt-1"><X className="w-3.5 h-3.5" /></button>}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">Procesverbali / Diskutimi</label>
                            <Textarea rows={4} value={form.minutes} onChange={(e) => setForm({ ...form, minutes: e.target.value })} placeholder="Çfarë u diskutua, kush mori pjesë…" data-testid="meet-form-minutes" />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest font-bold text-sage block mb-1">Konkluzioni</label>
                            <Textarea rows={3} value={form.conclusion} onChange={(e) => setForm({ ...form, conclusion: e.target.value })} placeholder="Përfundimet dhe vendimet e marra" data-testid="meet-form-conclusion" />
                        </div>
                        {err && <div className="text-xs text-terracotta">{err}</div>}
                        <DialogFooter>
                            <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="meet-form-submit">{busy ? "Duke ruajtur…" : "Ruaj"}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
